import {
  ExpertSystemEquipmentType,
  useCommentKpiCreate,
  useCommentKpiDelete,
  useCommentKpiEdit,
  useCommentKpi
} from '@domain/expert-system'
import {ValidationErrorKeys} from '@hconnect/common/consts'
import {useFundamentalsLanguage} from '@hconnect/common/hooks'
import {KpiCalculationError} from '@hconnect/common/types'
import {completionScoreBandColor} from '@hconnect/common/utils'
import {useUserContext} from '@hconnect/login'
import {COMMENTS_DATE_FORMAT, formatTimeZoneDate, getTimezoneName} from '@hconnect/uikit'
import {
  Alert,
  ControlledCommentBox,
  ControlledCommentProps,
  ProgressBar,
  spawnSuccessToast,
  spawnErrorToast
} from '@hm/fundamental'
import CloseIcon from '@mui/icons-material/Close'
import {Box, Dialog, DialogContent, DialogTitle, IconButton, Stack, Typography} from '@mui/material'
import {isNil} from 'lodash'
import moment from 'moment-timezone'
import React, {useEffect, useMemo, useState} from 'react'
import {useTranslation} from 'react-i18next'
import {NavigateFunction, useNavigate} from 'react-router'

import {FEATURE_NAMESPACE, monthMapStringToDigit, VALIDATION_TRANSLATIONS_KEY} from '../consts'
import {handleError} from '../hooks'
import {HierarchyTableNode} from '../types'

const COMMENT_MAX_LENGTH = 5000
const KILN_WARNING_THRESHOLD = 80
const MILL_WARNING_THRESHOLD = 85

// Regex to extract embedded KPI value metadata prefix, e.g. [[es-ctx:2026-04-07|87]]
// The day a comment belongs to is stored by the backend (`day`), the prefix only carries the KPI value at comment time.
const META_PREFIX_REGEX = /^\[\[es-ctx:([^|\]]+)(?:\|([^\]]*))?]\]\s*/

/** Narrows a raw stored string to a known KpiCalculationError value */
const KNOWN_KPI_ERRORS: ReadonlySet<string> = new Set(Object.values(KpiCalculationError))
const isKnownKpiError = (value: string): value is KpiCalculationError => KNOWN_KPI_ERRORS.has(value)

const computeCommentPill = (
  metaMatch: RegExpExecArray,
  language: string,
  t: (key: string) => string
): ControlledCommentProps<string>['pill'] => {
  const dateStr = metaMatch[1]
  const rawValue = metaMatch[2]
  const isDaily = dateStr.split('-').length === 3
  const pillDate = moment(isDaily ? dateStr : `${dateStr}-01`).locale(language)
  const dateDisplay = isDaily ? pillDate.format('D MMM') : pillDate.format('MMM YYYY')

  if (rawValue && isKnownKpiError(rawValue)) {
    return {type: 'info', text: `${dateDisplay} · ${t(VALIDATION_TRANSLATIONS_KEY[rawValue])}`}
  }
  if (rawValue) {
    const numericValue = Number(rawValue)
    let pillType: 'positive' | 'warning' | 'negative'
    if (numericValue > 80) {
      pillType = 'positive'
    } else if (numericValue > 50) {
      pillType = 'warning'
    } else {
      pillType = 'negative'
    }
    return {type: pillType, text: `${dateDisplay} · ${rawValue}%`}
  }
  return {type: 'neutral', text: dateDisplay}
}

type CommentKpiDialogProps = {
  item?: HierarchyTableNode
  progress?: number
  kpiCalculationError?: KpiCalculationError
  hierarchyId: string
  kpiTargetEquipmentType: ExpertSystemEquipmentType
  month: string
  year: number
  /** Set when opened from the daily view — the specific day number (1–31) the user clicked */
  selectedDay?: number
  onClose: () => void
}

export const CommentKpiDialog: React.FC<CommentKpiDialogProps> = ({
  item,
  progress,
  kpiCalculationError,
  hierarchyId,
  kpiTargetEquipmentType,
  month,
  year,
  selectedDay,
  onClose
}) => {
  const {
    t,
    i18n: {language}
  } = useTranslation(FEATURE_NAMESPACE)
  const {loggedUser} = useUserContext()
  const fundamentalsLanguage = useFundamentalsLanguage()
  const timezone = getTimezoneName()
  const navigate: NavigateFunction = useNavigate()

  const isCommentValid = (value: string): boolean => {
    if (value.length === 0) return false
    if (value.length > COMMENT_MAX_LENGTH) {
      spawnErrorToast(t(ValidationErrorKeys.MAX_LENGTH, {max: COMMENT_MAX_LENGTH}))
      return false
    }
    return true
  }

  const selectedDate = `${year}-${monthMapStringToDigit[month.toLowerCase()]}`
  const {data, error} = useCommentKpi(
    kpiTargetEquipmentType,
    hierarchyId,
    selectedDate,
    selectedDay
  )

  useEffect(() => {
    if (error) {
      handleError(error, navigate)
    }
  }, [error, navigate])

  const allComments: ControlledCommentProps<string>[] = useMemo(
    () =>
      [...(data ?? [])]
        .sort((a, b) => new Date(b.modifiedAt).getTime() - new Date(a.modifiedAt).getTime())
        .map((comment) => {
          const metaMatch = META_PREFIX_REGEX.exec(comment.content)
          const displayContent = metaMatch
            ? comment.content.slice(metaMatch[0].length)
            : comment.content
          const pill = metaMatch ? computeCommentPill(metaMatch, language, t) : undefined

          return {
            id: comment.id,
            date: formatTimeZoneDate(comment.modifiedAt, timezone, COMMENTS_DATE_FORMAT, language),
            content: displayContent,
            name: comment.author?.name ?? '',
            ownerId: comment.author?.id,
            isDeletable: comment.author?.id === loggedUser?.id,
            isEditable: comment.author?.id === loggedUser?.id,
            pill
          }
        }),
    [data, language, loggedUser?.id, t, timezone]
  )

  const {mutateAsync: createComment} = useCommentKpiCreate(
    kpiTargetEquipmentType,
    hierarchyId,
    selectedDate,
    {
      onError: (error) => {
        handleError(error, navigate)
      }
    }
  )

  const {mutateAsync: deleteComment} = useCommentKpiDelete(
    kpiTargetEquipmentType,
    hierarchyId,
    selectedDate,
    {
      onError: (error) => {
        handleError(error, navigate)
      }
    }
  )

  const {mutateAsync: editComment} = useCommentKpiEdit(
    kpiTargetEquipmentType,
    hierarchyId,
    selectedDate,
    {
      onError: (error) => {
        handleError(error, navigate)
      },
      onSuccess: () => {
        spawnSuccessToast(t('label.success'))
      }
    }
  )

  const [commentInputValue, setCommentInputValue] = useState('')

  const handleAddComment = async (comment: string) => {
    const trimmedComment = comment.trim()
    if (!isCommentValid(trimmedComment)) return

    // Embed context metadata so any reader knows the date and KPI value at comment time
    const dateStr = selectedDay
      ? `${selectedDate}-${String(selectedDay).padStart(2, '0')}`
      : selectedDate
    let valueStr: string
    if (isNil(progress)) {
      valueStr = kpiCalculationError ?? ''
    } else {
      valueStr = String(Math.round(progress))
    }
    const valuePart = valueStr ? `|${valueStr}` : ''
    const prefix = `[[es-ctx:${dateStr}${valuePart}]] `

    await createComment({content: `${prefix}${trimmedComment}`, day: selectedDay})
    spawnSuccessToast(t('label.success'))
    setCommentInputValue('')
  }

  const handleDeleteComment = async (id: string): Promise<void> => {
    const comment = data?.find((c) => c.id === id)
    if (comment?.author?.id !== loggedUser?.id) {
      spawnErrorToast(t('label.notAllowedToDelete'))
      return
    }
    await deleteComment({id})
    spawnSuccessToast(t('label.success'))
  }

  const handleEditComment = async (
    comment: ControlledCommentProps<string>,
    newContent: string
  ): Promise<void> => {
    const trimmed = newContent.trim()
    if (!isCommentValid(trimmed)) return
    const originalComment = data?.find((c) => c.id === comment.id)
    if (originalComment?.author?.id !== loggedUser?.id) {
      spawnErrorToast(t('label.editOwnCommentOnly'))
      return
    }
    const metaMatch = originalComment ? META_PREFIX_REGEX.exec(originalComment.content) : null
    const prefix = metaMatch ? metaMatch[0] : ''
    await editComment({id: comment.id, value: `${prefix}${trimmed}`})
  }

  return (
    <Dialog open={true} onClose={onClose} maxWidth="sm" data-test-id="comments-dialog" fullWidth>
      <Box display="flex" alignItems="center" justifyContent="space-between" pr={3} pt={3} gap={1}>
        <Stack pl={3}>
          <DialogTitle variant="h3" sx={{pl: 0, pb: 0, pt: 0}} data-test-id="info-dialog-title">
            {item?.name}
          </DialogTitle>
          <Typography variant="caption">{selectedDate}</Typography>
        </Stack>
        <IconButton aria-label="close" onClick={onClose} data-test-id="comments-dialog-close">
          <CloseIcon />
        </IconButton>
      </Box>
      {!isNil(progress) &&
        progress <
          (kpiTargetEquipmentType === ExpertSystemEquipmentType.Kiln
            ? KILN_WARNING_THRESHOLD
            : MILL_WARNING_THRESHOLD) && (
          <Box alignItems="center" gap={1} pt={2} pl={3} pr={3}>
            <Alert variant="warning">
              {t(
                kpiTargetEquipmentType === ExpertSystemEquipmentType.Kiln
                  ? 'label.warningCommentKiln'
                  : 'label.warningComment'
              )}
            </Alert>
          </Box>
        )}
      {kpiCalculationError && (
        <Box alignItems="center" gap={1} pt={2} pl={3} pr={3}>
          <Alert variant="info">{t(VALIDATION_TRANSLATIONS_KEY[kpiCalculationError])}</Alert>
        </Box>
      )}

      {!isNil(progress) && (
        <Box
          alignItems="center"
          gap={1}
          pt={2}
          px={3}
          sx={{
            '.monthly-progress-bar div.bg-disabled_background div.bg-primary': {
              backgroundColor: `${completionScoreBandColor(progress)}`
            }
          }}
        >
          <ProgressBar
            label={t('commentHeader')}
            currentValue={progress}
            valueLabel={`${progress}%`}
            className="monthly-progress-bar"
            size={'medium'}
            maxValue={100}
          />
        </Box>
      )}

      <DialogContent sx={{overflow: 'auto'}}>
        <ControlledCommentBox
          data-cy="comment-component"
          language={fundamentalsLanguage}
          comments={allComments}
          commentInputValue={commentInputValue}
          onCommentInputChange={setCommentInputValue}
          onSubmit={handleAddComment}
          onEdit={handleEditComment}
          onDelete={handleDeleteComment}
        />
      </DialogContent>
    </Dialog>
  )
}
